/**
 * Type-safe feature flag hooks for SMS.
 *
 * Fetches pre-computed feature states from the backend.
 * NO CLIENT-SIDE LOGIC — backend computes everything via has_feature().
 *
 * Scoping rule:
 * - When `activeOrganization` is set (vendor or Aegix-internal masquerade
 *   via VendorOrgSelector), feature gates respect ONLY that org's sites.
 *   This is what drives the "auto-filter all menu items to the currently
 *   selected org" UX in the page header.
 * - Otherwise (regular user, no dropdown), the previous union-across-sites
 *   behavior applies — SMS is an admin tool that manages across sites.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useActiveOrganization } from '@aegix/auth-organization';
import type { ActiveOrganization } from '../lib/sms-auth';
import { getFeatureEnabled } from '../services/feature.service';
import type { FeatureCode } from '../types/features';
import type { SiteFeatures } from '../types/licensing';

const FEATURE_QUERY_KEY = ['featureEnabled'] as const;

function scopeToActiveOrg(
  sitesFeatures: SiteFeatures[] | undefined,
  activeOrgId: number | null | undefined,
): SiteFeatures[] {
  if (!sitesFeatures) return [];
  if (activeOrgId == null) return sitesFeatures;
  return sitesFeatures.filter((s) => s.organization_id === activeOrgId);
}

/**
 * Check if a feature is enabled.
 * - Scoped to `activeOrganization` when set; otherwise unions across sites.
 *
 * @param code - FeatureCode enum value to check
 * @returns boolean - true if feature is enabled for the scoped sites
 */
export function useFeature(code: FeatureCode): boolean {
  // SMS organization ids are numeric; the lib widens to `string | number`.
  // Cast at the consume site to keep `scopeToActiveOrg`'s `number` signature.
  const activeOrganization =
    useActiveOrganization() as ActiveOrganization | null;
  const { data: sitesFeatures } = useQuery<SiteFeatures[]>({
    queryKey: FEATURE_QUERY_KEY,
    queryFn: getFeatureEnabled,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const scoped = scopeToActiveOrg(sitesFeatures, activeOrganization?.id);
    if (scoped.length === 0) return false;
    return scoped.some((s) => s.features?.[code] === true);
  }, [code, sitesFeatures, activeOrganization?.id]);
}

/** Which unit a scoped feature lookup resolves against. */
export type FeatureScopeTier = 'site' | 'organization';

/**
 * Check if a feature is enabled for ONE named site or organization.
 *
 * `useFeature` above answers "may this user see the menu item at all?" — it
 * unions across every visible site whenever no `activeOrganization` is
 * selected, which is right for navigation and wrong for an editor bound to a
 * specific scope. An Aegix-internal admin with no active-org selection manages
 * districts X and Y; if X has the flag and Y does not, the union offers Y's
 * grid options X paid for, and Y's writes 400.
 *
 * So: resolve against the scope the surface is actually editing, and fail
 * closed — an unknown scope id, an unresolvable one (no matching entry in the
 * payload), or a not-yet-loaded payload all read as OFF, never as a union.
 *
 * `every` rather than `some` because the scope is the unit: feature state is
 * computed per organization on the backend, so every site under one
 * organization agrees. Any disagreement means the scope did not resolve to
 * what the caller meant, and the fail-closed answer is the safe one.
 *
 * @param code - FeatureCode enum value to check
 * @param tier - resolve `scopeId` as a site id or an organization (district) id
 * @param scopeId - the site / organization the calling surface writes to
 */
export function useScopedFeature(
  code: FeatureCode,
  tier: FeatureScopeTier,
  scopeId: number | null | undefined,
): boolean {
  const { data: sitesFeatures } = useQuery<SiteFeatures[]>({
    queryKey: FEATURE_QUERY_KEY,
    queryFn: getFeatureEnabled,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    if (scopeId == null || !sitesFeatures) return false;
    const scoped = sitesFeatures.filter((s) =>
      tier === 'site' ? s.site_id === scopeId : s.organization_id === scopeId,
    );
    if (scoped.length === 0) return false;
    return scoped.every((s) => s.features?.[code] === true);
  }, [code, tier, scopeId, sitesFeatures]);
}

/**
 * Get all feature states.
 * - Scoped to `activeOrganization` when set; otherwise unions across sites.
 */
export function useFeatures(): Record<string, boolean> {
  // SMS organization ids are numeric; the lib widens to `string | number`.
  // Cast at the consume site to keep `scopeToActiveOrg`'s `number` signature.
  const activeOrganization =
    useActiveOrganization() as ActiveOrganization | null;
  const { data: sitesFeatures } = useQuery<SiteFeatures[]>({
    queryKey: FEATURE_QUERY_KEY,
    queryFn: getFeatureEnabled,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const scoped = scopeToActiveOrg(sitesFeatures, activeOrganization?.id);
    if (scoped.length === 0) return {};

    const union: Record<string, boolean> = {};
    for (const site of scoped) {
      for (const [key, value] of Object.entries(site.features ?? {})) {
        if (value) union[key] = true;
      }
    }
    return union;
  }, [sitesFeatures, activeOrganization?.id]);
}

/**
 * Hook to check if features data is still loading.
 */
export function useFeaturesLoading(): boolean {
  const { isLoading } = useQuery<SiteFeatures[]>({
    queryKey: FEATURE_QUERY_KEY,
    queryFn: getFeatureEnabled,
    staleTime: 5 * 60 * 1000,
  });
  return isLoading;
}
